import {
  createCitationReport,
  getCitationReport,
  addClaim,
  updateQuery,
  searchClaim,
  startCitationReport as startCitationReportApi,
} from '../api';
import { CitationReportFromApi } from '../types';

export class CiterightDeferred {
  resolve: (report: CitationReportFromApi) => void;
  reject: (error: Error) => void;

  updateCallbacks: Array<(report: CitationReportFromApi) => void> = [];

  promise: Promise<CitationReportFromApi>;

  private _isStopped: boolean = false;
  get isStopped(): boolean {
    return this._isStopped;
  }

  onStop: () => void;

  constructor(onStop: () => void) {
    this.onStop = onStop;
    this.resolve = () => {
      console.error('CiterightDeferred: resolve called but not implemented');
    };
    this.reject = () => {
      console.error('CiterightDeferred: reject called but not implemented');
    };
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });

    this.onStop = onStop;
  }

  dispatchUpdate(report: CitationReportFromApi): void {
    this.updateCallbacks.forEach((callback) => callback(report));
  }

  onUpdate(callback: (report: CitationReportFromApi) => void): () => void {
    this.updateCallbacks.push(callback);
    return () => {
      this.updateCallbacks = this.updateCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  stop(): void {
    this._isStopped = true;
    this.onStop();
  }
}

export const startCitationReport = (reportId: number) => {
  return flattenPromiseToDeferred(
    startCitationReportApi(reportId).then((response) => {
      return pollCiterightSession({ reportId: response.report.report_id });
    }),
  );
};

export const createCiterightReport = (document: string, rawFile?: File) => {
  return flattenPromiseToDeferred(
    createCitationReport(document, rawFile).then((response) => {
      return pollCiterightSession({
        reportId: response.report.report_id,
      });
    }),
  );
};

export const addClaimToCiterightSession = (
  reportId: number,
  claim: string,
  publicToken?: string,
) => {
  return flattenPromiseToDeferred(
    addClaim(String(reportId), claim, publicToken).then(() => {
      return pollCiterightSession(publicToken ? { publicToken } : { reportId });
    }),
  );
};

export const updateQueryInCiterightSession = (
  reportId: number,
  claimId: string,
  newQuery: string,
  publicToken?: string,
) => {
  return flattenPromiseToDeferred(
    updateQuery(String(reportId), claimId, newQuery, publicToken).then(() => {
      return pollCiterightSession(publicToken ? { publicToken } : { reportId });
    }),
  );
};

export const searchClaimInCiterightSession = (
  reportId: number,
  claimId: string,
  publicToken?: string,
) => {
  return flattenPromiseToDeferred(
    searchClaim(String(reportId), claimId, publicToken).then(() => {
      return pollCiterightSession(publicToken ? { publicToken } : { reportId });
    }),
  );
};

const flattenPromiseToDeferred = (
  promise: Promise<CiterightDeferred>,
): CiterightDeferred => {
  let wrappedDeferred: CiterightDeferred | null = null;
  let cleanupUpdate: (() => void) | null = null;
  let stopped = false;
  const wrappingDeferred = new CiterightDeferred(() => {
    stopped = true;
    if (cleanupUpdate) {
      cleanupUpdate();
      cleanupUpdate = null;
    }
    if (wrappedDeferred) {
      wrappedDeferred.stop();
      wrappedDeferred = null;
    }
  });
  promise.then((deferred) => {
    if (stopped) {
      deferred.stop();
      return;
    }
    wrappedDeferred = deferred;
    wrappedDeferred.promise.then(wrappingDeferred.resolve);
    wrappedDeferred.promise.catch(wrappingDeferred.reject);
    cleanupUpdate = wrappedDeferred.onUpdate((report) => {
      wrappingDeferred.dispatchUpdate(report);
    });
  });
  return wrappingDeferred;
};

interface PollCiterightSessionOptions {
  pollingInterval?: number;
  pollingTimeout?: number;
  sessionId?: string;
  reportId?: number;
  publicToken?: string;
}

export const pollCiterightSession = (
  options: PollCiterightSessionOptions,
): CiterightDeferred => {
  const {
    pollingInterval = 3000,
    pollingTimeout,
    sessionId,
    reportId,
    publicToken,
  } = options;

  let currentReport: CitationReportFromApi | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  let isComplete = false;
  let isStopped = false;

  const cleanup = () => {
    isStopped = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    currentReport = null;
  };

  const deferred = new CiterightDeferred(cleanup);

  const pollStatus = async (_sessionId?: string, reportId?: number) => {
    if (isStopped || deferred.isStopped) return;

    try {
      let response: { report: CitationReportFromApi };
      if (publicToken) {
        response = await getCitationReport(undefined, publicToken);
      } else if (reportId) {
        response = await getCitationReport(String(reportId));
      } else {
        throw new Error('No report ID provided');
      }

      const newReport = response.report;
      if (JSON.stringify(currentReport) !== JSON.stringify(newReport)) {
        deferred.dispatchUpdate(newReport);
      }
      currentReport = newReport;

      const allJobsComplete = newReport.jobs.every(
        (job) => job.completed_at || job.error_at,
      );

      if (allJobsComplete && !isComplete) {
        isComplete = true;
        cleanup();
        deferred.stop();
        deferred.resolve(newReport);
      } else if (!isStopped) {
        // Schedule next poll if not complete and not stopped
        scheduleNextPoll();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to check citation status';

      const errorObj = error instanceof Error ? error : new Error(errorMessage);

      // Reject the promise if not already complete
      if (!isComplete && !isStopped && !deferred.isStopped) {
        cleanup();
        deferred.stop();
        deferred.reject(errorObj);
      }
    }
  };

  const scheduleNextPoll = () => {
    if (isStopped || deferred.isStopped || isComplete) {
      return;
    }

    // Check if polling timeout has been reached
    if (
      pollingTimeout &&
      startTime &&
      Date.now() - startTime >= pollingTimeout
    ) {
      cleanup();
      deferred.stop();
      deferred.reject(new Error(`Polling timeout after ${pollingTimeout}ms`));
      return;
    }

    // Clear any existing timeout before scheduling a new one
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      pollStatus(sessionId, reportId);
    }, pollingInterval);
  };

  const startTime = Date.now();
  pollStatus(sessionId, reportId);

  return deferred;
};
